import { supabase } from '../../supabase';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { GGSwapShift } from '@shiftly/shared';
import { DateTime } from 'luxon';
import { convertDateFields } from './supabaseGGSwapShiftService';
import { useAuth } from '../../../../contexts/AuthContext';
import { Permissions } from '@shiftly/shared/src/permissions';

export interface RealtimeGGSwapShiftManagerOptions {
  enabled?: boolean;
  onError?: (error: any) => void;
}

export const useRealtimeGGSwapShiftManager = (options: RealtimeGGSwapShiftManagerOptions = {}) => {
  const { enabled = true, onError } = options;
  const { user, permissions, session, loading } = useAuth();
  const queryClient = useQueryClient();
  const subscriptionRef = useRef<any>(null);

  // Only enable if auth is ready, user is authenticated, and has GG_SWAP_MANAGE permission
  const shouldEnable = enabled && !loading && !!user && !!session && permissions.includes(Permissions.GG_SWAP_MANAGE);

  const updateGGSwapShiftQueries = useCallback((payload: any) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;

    const ggSwapShiftQueries = queryClient.getQueriesData<GGSwapShift[]>({
      queryKey: ['supabase-gg-swap-shifts']
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('Supabase realtime GG swap shift update:', { eventType, recordId: newRecord?.id || oldRecord?.id });
    }

    ggSwapShiftQueries.forEach(([queryKey, oldData]) => {
      if (!Array.isArray(oldData)) return;

      queryClient.setQueryData(queryKey, (currentData: GGSwapShift[] | undefined) => {
        if (!currentData) return currentData;

        switch (eventType) {
          case 'INSERT': {
            const shift = convertDateFields(newRecord);
            if (shouldIncludeGGSwapShiftInQuery(shift, queryKey)) {
              const exists = currentData.some(existingShift => existingShift.id === shift.id);
              if (!exists) {
                return [...currentData, shift].sort((a, b) =>
                  a.date.toMillis() - b.date.toMillis()
                );
              }
            }
            return currentData;
          }

          case 'UPDATE': {
            const shift = convertDateFields(newRecord);
            if (shouldIncludeGGSwapShiftInQuery(shift, queryKey)) {
              const existingIndex = currentData.findIndex(existingShift => existingShift.id === shift.id);
              if (existingIndex >= 0) {
                const newData = [...currentData];
                newData[existingIndex] = shift;
                return newData.sort((a, b) => a.date.toMillis() - b.date.toMillis());
              } else {
                return [...currentData, shift].sort((a, b) =>
                  a.date.toMillis() - b.date.toMillis()
                );
              }
            } else {
              return currentData.filter(existingShift => existingShift.id !== shift.id);
            }
          }

          case 'DELETE': {
            return currentData.filter(shift => shift.id !== oldRecord.id);
          }

          default:
            return currentData;
        }
      });
    });
  }, [queryClient]);

  const shouldIncludeGGSwapShiftInQuery = (shift: GGSwapShift, queryKey: readonly unknown[]): boolean => {
    const queryParams = queryKey[1] as any;
    if (!queryParams?.filters) return true;

    return queryParams.filters.every((filter: any) => {
      const { field, operator, value } = filter;
      const shiftValue = getNestedValue(shift, field);

      switch (operator) {
        case 'eq':
          return shiftValue === value;
        case 'neq':
          return shiftValue !== value;
        case 'gt':
          if (DateTime.isDateTime(shiftValue)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shiftValue.toMillis() > compareValue.toMillis();
          }
          return shiftValue > value;
        case 'gte':
          if (DateTime.isDateTime(shiftValue)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shiftValue.toMillis() >= compareValue.toMillis();
          }
          return shiftValue >= value;
        case 'lt':
          if (DateTime.isDateTime(shiftValue)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shiftValue.toMillis() < compareValue.toMillis();
          }
          return shiftValue < value;
        case 'lte':
          if (DateTime.isDateTime(shiftValue)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shiftValue.toMillis() <= compareValue.toMillis();
          }
          return shiftValue <= value;
        case 'in':
          return Array.isArray(value) && value.includes(shiftValue);
        case 'not-in':
          return Array.isArray(value) && !value.includes(shiftValue);
        default:
          return true;
      }
    });
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  useEffect(() => {
    if (!shouldEnable) return;

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
        subscriptionRef.current = supabase
          .channel('realtime gg swap shift subscription', { config: { private: true } })
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'gg_swap_shifts',
            },
            (payload) => {
              updateGGSwapShiftQueries(payload);
            }
          )
          .subscribe((status, err) => {
            console.log('Realtime GG swap shift subscription status:', status);
            if (err && onError) {
              onError(err);
            }
          });
      } else {
        console.log('User signed out, cleaning up realtime GG swap shift subscription');
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        }
      }
    });

    return () => {
      console.log('Cleaning up realtime GG swap shift subscription');
      listener?.subscription.unsubscribe();
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [shouldEnable, updateGGSwapShiftQueries, onError]);

  return {
    isConnected: !!subscriptionRef.current
  };
};